import React, { useState, useRef } from 'react';

import { Button, Grid, Link } from 'components/ui';
import Modal from 'components/Modal/Modal';
import FrictionlessAddressForm from 'components/Addresses/FrictionlessAddressForm';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import ErrorList from 'components/ErrorList';

import useAddressValidation from 'components/RichProfile/MyAccount/AutoReplenishment/ManageAddress/hooks/useAddressValidation';

import FormsUtils from 'utils/Forms';
import userUtils from 'utils/User';
import { wrapFunctionalComponent } from 'utils/framework';

/**
 *
 * @param {string[]} errorMessages
 * @param {boolean} isOpen
 * @param {function} onDismiss
 * @param {{modalTitle: string, setAsDefault: string, enterAddressManually: string, cancel: string, confirm: string }} localization
 * @param {function} onAddressSelected
 * @param {function} onBeforeValidation - removes errors from back-end validation
 * @param {function} validateAddress
 * @param {object} selectedAddress
 * @returns {React.JSX.Element}
 * @constructor
 */
function AddNewAddressModal({
    errorMessages = null,
    isOpen = false,
    localization = {},
    onAddressSelected = null,
    onBeforeValidation = null,
    onDismiss = null,
    selectedAddress,
    validateAddress = null
}) {
    const [isShortenedLoqateForm, setIsShortenedLoqateForm] = useState(!selectedAddress);
    const [isDefault, setIsDefault] = useState(selectedAddress?.isDefault ?? false);
    const [isManualEntry, setIsManualEntry] = useState(true);

    const handleIsDefault = () => setIsDefault(prevIsDefault => !prevIsDefault);

    const addressFormRef = useRef();
    const shouldHiddenLoqate = useRef(!!selectedAddress);

    const shipCountry = userUtils.getShippingCountry().countryCode;

    const { isHidden, validateAddressForm } = useAddressValidation({
        addressFormRef,
        isDefault,
        isManualEntry,
        onAddressSelected,
        onBeforeValidation,
        validateAddress
    });

    const handleManualEntry = () => {
        shouldHiddenLoqate.current = true;
        setIsShortenedLoqateForm(false);
    };

    const closeModal = () => {
        shouldHiddenLoqate.current = false;
        onDismiss?.();
    };

    const onSubmitLoqateAddress = () => {
        setIsShortenedLoqateForm(false);
        setIsManualEntry(false);
    };

    return (
        <Modal
            isOpen={isOpen}
            isHidden={isHidden}
            onDismiss={closeModal}
            hasBodyScroll={true}
            width={0}
            focusDialog={true}
            isDrawer={true}
            customStyle={{
                inner: {
                    minHeight: '645px'
                }
            }}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>

            <Modal.Body overflowY='unset'>
                <div id='ship_address_form'>
                    <ErrorList errorMessages={errorMessages} />
                    <FrictionlessAddressForm
                        editStore={FormsUtils.getStoreEditSectionName(FormsUtils.FORMS.CHECKOUT.SHIPPING_ADDRESS, false)}
                        isEditMode={!!selectedAddress}
                        address={selectedAddress}
                        country={selectedAddress?.country ?? shipCountry}
                        onRef={comp => (addressFormRef.current = comp)}
                        shouldHiddenLoqate={shouldHiddenLoqate.current}
                        isCountryFieldHidden={true}
                        showAddress2Input={true}
                        forceSingleColumn={true}
                        isShortenedLoqateForm={isShortenedLoqateForm}
                        onSubmitLoqateAddress={onSubmitLoqateAddress}
                    />

                    {!isShortenedLoqateForm && (
                        <Checkbox
                            checked={isDefault}
                            onClick={handleIsDefault}
                            name='is_default'
                            marginBottom={4}
                        >
                            {localization.setAsDefault}
                        </Checkbox>
                    )}
                </div>

                {isShortenedLoqateForm && (
                    <Link
                        color='link'
                        children={localization.enterAddressManually}
                        onClick={handleManualEntry}
                        marginBottom={4}
                    />
                )}
            </Modal.Body>

            <Modal.Footer
                hasBorder={true}
                zIndex='0'
            >
                <Grid
                    gap={4}
                    gridTemplateColumns={['1fr 1fr']}
                >
                    <Button
                        variant='secondary'
                        data-at={Sephora.debug.dataAt('address_add_cancel')}
                        onClick={closeModal}
                        children={localization.cancel}
                        minWidth={true}
                    />
                    <Button
                        variant='primary'
                        data-at={Sephora.debug.dataAt('address_add_confirm')}
                        onClick={validateAddressForm}
                        children={localization.confirm}
                    />
                </Grid>
            </Modal.Footer>
        </Modal>
    );
}

export default wrapFunctionalComponent(AddNewAddressModal, 'AddNewAddressModal');
